import { Button, Card, FormControl, Grid, InputLabel, MenuItem, Select, styled, TextField, Typography, } from '@mui/material';
import { useContext, useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import SnackbarContext from 'contexts/snackbarContext';
import { createImpactReportScheduling, getImpactReportSchedulingById, modifyImpactReportScheduling } from 'services/impactReportScheduling.services';

import ErrorText from 'components/ErrorText/ErrorText';
import { addMonths, startOfDay, startOfMonth } from 'date-fns';
import DatePicker from 'react-date-picker';
import { theme } from 'theme';
import { ImpactReportModelType } from 'types/impactReportModel.type';
import { CreateImpactReportSchedulingType, ModifyImpactReportSchedulingType } from 'types/impactReportScheduling.type';
import { ImpactReportTemplateType } from 'types/impactReportTemplate.type';
import { OrganisationType } from 'types/organisations.type';

type CreateImpactReportSchedulingFormType = {
  name:string;
  description:string;
  
  organisation: string;
  template: string;
  model: string;

  generationFrequency: string;
  generationMode: 'merged' | 'individual';

  emailtext : string;
  emailSubject : string;
  recipients: string | string[];
};

const CustomForm = styled('form')`width: 100%;`;

interface CreateImpactReportSchedulingFormProps {
  impactReportId?: string;
  organisationList: OrganisationType[];
  impactReportTemplateList: ImpactReportTemplateType[];
  impactReportModelList: ImpactReportModelType[];
}

export default function CreateImpactReportSchedulingForm({impactReportId, organisationList, impactReportTemplateList, impactReportModelList} : CreateImpactReportSchedulingFormProps) {
  const { t } = useTranslation();
  const {handleSubmit, formState: { errors }, setValue, control, watch,} = useForm<CreateImpactReportSchedulingFormType>();
  const { handleOpen } = useContext(SnackbarContext);
  const { id } = useParams();
  const navigate = useNavigate();

  useEffect(() => {
    if (id) {
      getImpactReportSchedulingById(id).then((data) => {

        setValue('name', data.name);
        setValue('description', data.description);

        setValue('emailtext', data.emailtext);
        setValue('emailSubject', data.emailSubject);
        setValue('recipients', data.recipients);

        setValue('generationFrequency', data.generationFrequency);
        setValue('generationMode', data.generationMode);

        setValue('organisation', data.organisation.id);
        setValue('template', data.template.id);
        setValue('model', data.model.id);
      });
    }
  }, [id]);

  const onSubmit = (data: CreateImpactReportSchedulingFormType) => {
    if (!id) {
      const impactReprotSchedulingObject: CreateImpactReportSchedulingType = {
        name: data.name,
        description: data.description,
        generationFrequency: data.generationFrequency,
        generationMode: data.generationMode,
        organisationId: data.organisation,
        templateId: data.template,
        modelId: data.model,

        emailtext: data.emailtext,
        emailSubject: data.emailSubject,
        recipients: data.recipients?.length
          ? (typeof data.recipients === 'string'
            ? data.recipients.split(',').map((r) => r.trim()).filter(Boolean)
            : data.recipients)
          : [],
      };
  
      createImpactReportScheduling(impactReprotSchedulingObject)
        .then((result) => {
          handleOpen(t('impactReportSchedulingForm.impactReportSchedulingCreatedSuccessfullyMessage'), 'success',);
          navigate('/impactReportSchedulings/'+result.data.id);
        })
        .catch(({ data }) => {
          handleOpen(data.message);
        });
    }
    else {

      const modifyImpactReportSchedulingObject: ModifyImpactReportSchedulingType = {
        id: id,
        name: data.name,
        description: data.description,

        emailtext: data.emailtext,
        emailSubject: data.emailSubject,
        recipients: data.recipients?.length
          ? (typeof data.recipients === 'string'
            ? data.recipients.split(',').map((r: string) => r.trim()).filter(Boolean)
            : data.recipients)
          : [],
        generationFrequency: data.generationFrequency,
        generationMode: data.generationMode,

        modelId: data.model,
        organisationId: data.organisation,
        templateId: data.template,
      };

      modifyImpactReportScheduling(id, modifyImpactReportSchedulingObject)
        .then((result) => {
          handleOpen(t('impactReportSchedulingForm.impactReportSchedulingModifiedSuccessfullyMessage'), 'success',);

          getImpactReportSchedulingById(id).then((data) => {

            setValue('name', data.name);
            setValue('description', data.description);
    
            setValue('generationFrequency', data.generationFrequency);
            setValue('generationMode', data.generationMode);
    
            setValue('emailtext', data.emailtext);
            setValue('emailSubject', data.emailSubject);
            setValue('recipients', data.recipients);

            setValue('organisation', data.organisation.id);
            setValue('template', data.template.id);
            setValue('model', data.model.id);
          });
          
        })
        .catch(({ data }) => {
          handleOpen(data.message);
        });

    }
  };

  const checkKeyDown = (e: React.KeyboardEvent<HTMLElement>) => {
    if (e.code === 'Enter') e.preventDefault();
  };

  return (
    <Card sx={{ padding: '40px', maxWidth: '800px' }}>
      <CustomForm onSubmit={handleSubmit(onSubmit)} onKeyDown={checkKeyDown}>
        <Grid container spacing={2}>



          <Grid item xs={12}><Controller control={control} name="name" defaultValue="" render={({ field }) => ( <TextField {...field} fullWidth label={t('formFields.name')} type="text" inputProps={{ maxLength: 30 }} error={errors.name ? true : false} /> )} /> {errors.name && (<ErrorText>{t('formErrors.companyNameRequired')}</ErrorText>)}</Grid>
          <Grid item xs={12}><Controller control={control} defaultValue="" name="description" render={({ field: { value, onChange } }) => (<TextField onChange={onChange} fullWidth variant="outlined" type="text" value={value} label={t('formFields.description')} />)}/></Grid>

          <Grid item xs={6}>
            <FormControl fullWidth variant="outlined">
              <InputLabel id="generationFrequency-label">{t('formFields.generationFrequency')}</InputLabel>
              <Controller
                name="generationFrequency"
                control={control}
                defaultValue="monthly"
                render={({ field }) => (
                  <Select
                    {...field}
                    labelId="generationFrequency-label"
                    label={t('formFields.generationFrequency')}
                  >
                    <MenuItem value="monthly">{t('impactReportSchedulingForm.monthly')}</MenuItem>
                    <MenuItem value="quaterly">{t('impactReportSchedulingForm.quaterly')}</MenuItem>
                    <MenuItem value="yearly">{t('impactReportSchedulingForm.yearly')}</MenuItem>
                  </Select>
                )}
              />
            </FormControl>
          </Grid>
          <Grid item xs={6}>
            <FormControl fullWidth variant="outlined">
              <InputLabel id="generationMode-label">{t('formFields.generationMode')}</InputLabel>
              <Controller
                name="generationMode"
                control={control}
                defaultValue="merged"
                render={({ field }) => (
                  <Select
                    {...field}
                    labelId="generationMode-label"
                    label={t('formFields.generationMode')}
                  >
                    <MenuItem value="merged">{t('impactReportSchedulingForm.merged')}</MenuItem>
                    <MenuItem value="individual">{t('impactReportSchedulingForm.individual')}</MenuItem>
                  </Select>
                )}
              />
            </FormControl>
          </Grid>
          <Grid item xs={12}><Controller control={control} defaultValue = "" name="emailtext" render={({ field: { value, onChange } }) => (<TextField onChange={onChange} fullWidth variant="outlined" type="text" value={value} label={t('formFields.emailtext')} />)}/></Grid>
          <Grid item xs={12}><Controller control={control} defaultValue = "" name="emailSubject" render={({ field: { value, onChange } }) => (<TextField onChange={onChange} fullWidth variant="outlined" type="text" value={value} label={t('formFields.emailSubject')} />)}/></Grid>
          <Grid item xs={12}><Controller control={control}   defaultValue={[]} name="recipients" render={({ field: { value, onChange } }) => (<TextField onChange={onChange} fullWidth variant="outlined" type="text" value={value} label={t('formFields.recipients')} />)}/></Grid>

          <Grid item xs={12}>
            <FormControl fullWidth variant="outlined">
              <InputLabel htmlFor="label">{t('formFields.organisation')}</InputLabel>
              <Controller
                name="organisation"
                defaultValue = ""
                control={control}
                render={({ field: { onChange, value } }) => (
                  <Select value={value} onChange={onChange} label={t('formFields.organisation')} labelId="label" error={errors.organisation ? true : false}>
                    <MenuItem></MenuItem>
                    {organisationList.map((organisation: OrganisationType) => {return (<MenuItem key={organisation?.id}value={organisation?.id}>{organisation?.companyName}</MenuItem>);})}
                  </Select>
                )}
              />
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <FormControl fullWidth variant="outlined">
              <InputLabel htmlFor="label">{t('formFields.template')}</InputLabel>
              <Controller
                name="template"
                defaultValue = ""
                control={control}
                render={({ field: { onChange, value } }) => (
                  <Select value={value} onChange={onChange} label={t('formFields.template')} labelId="label" error={errors.template ? true : false}>
                    <MenuItem></MenuItem>
                    {impactReportTemplateList.map((impactReportTemplate: ImpactReportTemplateType) => {return (<MenuItem key={impactReportTemplate?.id}value={impactReportTemplate?.id}>{impactReportTemplate?.name}</MenuItem>);})}
                  </Select>
                )}
              />
            </FormControl>
          </Grid>
          
          <Grid item xs={12}>
            <FormControl fullWidth variant="outlined">
              <InputLabel htmlFor="label">{t('formFields.model')}</InputLabel>
              <Controller
                name="model"
                defaultValue = ""
                control={control}
                disabled={true}
                render={({ field: { onChange, value } }) => (
                  <Select value={value} onChange={onChange} label={t('formFields.model')} labelId="label" error={errors.model ? true : false}>
                    <MenuItem></MenuItem>
                    {impactReportModelList.map((impactReportModel: ImpactReportModelType) => {return (<MenuItem key={impactReportModel?.id}value={impactReportModel?.id}>{impactReportModel?.name}</MenuItem>);})}
                  </Select>
                )}
              />
            </FormControl>
          </Grid>

        </Grid>

        <Button variant="contained" sx={{ marginTop: '20px' }} type="submit">{t('formFields.submitButtonLabel')}</Button>
      </CustomForm>
    </Card>
  );
}
